import { ChannelConfig } from '../../config/websocketChannels';
import { STOMP_CONFIG } from './config';
import { StompClock } from './types';

export type InboundWorkItem = {
  key: string;
  channel: string;
  /**
   * Items sharing a non-null coalesce key collapse onto the newest payload.
   * `null` means every message must reach the store.
   */
  coalesceKey: string | null;
  parsed: any;
  originalData: any;
  config: ChannelConfig;
  bytes?: number;
  coalescedCount: number;
};

export type InboundDispatcherOptions = {
  clock: StompClock;
  onFlush: (items: InboundWorkItem[]) => void;
  onCoalesced?: (channel: string, droppedCount: number) => void;
  maxLatencyMs?: number;
  maxPendingItems?: number;
};

/**
 * Buffers inbound STOMP work and hands it to the store one batch per animation
 * frame, so a burst of messages costs a single React render pass instead of one
 * per message. Arrival order is preserved; `latest` items update in place.
 */
export class InboundDispatcher {
  private pending: InboundWorkItem[] = [];
  private byCoalesceKey = new Map<string, InboundWorkItem>();
  private frameHandle: any = null;
  private timeoutHandle: any = null;
  private clock: StompClock;
  private onFlush: (items: InboundWorkItem[]) => void;
  private onCoalesced?: (channel: string, droppedCount: number) => void;
  private maxLatencyMs: number;
  private maxPendingItems: number;

  constructor(options: InboundDispatcherOptions) {
    this.clock = options.clock;
    this.onFlush = options.onFlush;
    this.onCoalesced = options.onCoalesced;
    this.maxLatencyMs =
      options.maxLatencyMs ?? STOMP_CONFIG.inboundMaxLatencyMs;
    this.maxPendingItems =
      options.maxPendingItems ?? STOMP_CONFIG.inboundMaxPendingItems;
  }

  submit(item: InboundWorkItem) {
    if (item.coalesceKey !== null) {
      const existing = this.byCoalesceKey.get(item.coalesceKey);
      if (existing) {
        // Update in place so the batch keeps its arrival ordering and no new
        // object is allocated per superseded message.
        existing.parsed = item.parsed;
        existing.originalData = item.originalData;
        existing.bytes = item.bytes;
        existing.coalescedCount += 1;
        this.onCoalesced?.(item.channel, 1);
        this.schedule();
        return;
      }
      this.byCoalesceKey.set(item.coalesceKey, item);
    }

    this.pending.push(item);
    if (this.pending.length >= this.maxPendingItems) {
      // Backpressure: flush now rather than let the buffer grow. Nothing is
      // dropped, the batch is just larger than one frame's worth.
      this.flush();
      return;
    }
    this.schedule();
  }

  flush() {
    this.clearHandles();
    if (this.pending.length === 0) {
      return;
    }

    const batch = this.pending;
    this.pending = [];
    this.byCoalesceKey.clear();
    this.onFlush(batch);
  }

  cancel() {
    this.clearHandles();
    this.pending = [];
    this.byCoalesceKey.clear();
  }

  get pendingCount() {
    return this.pending.length;
  }

  private schedule() {
    if (this.frameHandle == null) {
      this.frameHandle = this.clock.scheduleFrame(() => {
        this.frameHandle = null;
        this.flush();
      });
    }
    // Animation frames do not run while the tab is hidden. A timer races the
    // frame so buffered work still reaches the store and cannot pile up.
    if (this.timeoutHandle == null) {
      this.timeoutHandle = this.clock.setTimeout(() => {
        this.timeoutHandle = null;
        this.flush();
      }, this.maxLatencyMs);
    }
  }

  private clearHandles() {
    if (this.frameHandle != null) {
      this.clock.cancelFrame(this.frameHandle);
      this.frameHandle = null;
    }
    if (this.timeoutHandle != null) {
      this.clock.clearTimeout(this.timeoutHandle);
      this.timeoutHandle = null;
    }
  }
}
